import React, { useEffect, useRef, useState } from 'react';
import { createPortal } from 'react-dom';
import { api } from '../lib/api';
import { T, serif, fmt, Boton, Campo, estiloInput, Aviso } from '../lib/ui';
import { procesarFotoFirma } from '../lib/firma';

// ════════════════════════════════════════════════════════════════════
// Impresion y firma de documentos internos
//
// Sobre el valor de la firma en pantalla: no equivale a una firma
// digital certificada. En el Peru eso exige certificado de entidad
// acreditada ante INDECOPI (Ley 27269). Lo que aporta es evidencia de
// control interno con sello de tiempo y huella del contenido.
//
// La planilla de movilidad es caso aparte: el Reglamento del Impuesto
// a la Renta exige firma manuscrita del trabajador, asi que ahi la
// plataforma obliga a imprimir y firmar en papel.
// ════════════════════════════════════════════════════════════════════

// ── PAD DE FIRMA ────────────────────────────────────────────────────
export function PadFirma({ onGuardar, onCancelar, titulo = 'Firma', nombreFijo, dniInicial }) {
  const lienzoRef = useRef(null);
  const archivoRef = useRef(null);
  const [modo, setModo] = useState('dibujar');   // dibujar | foto
  const [vacio, setVacio] = useState(true);
  const [nombre, setNombre] = useState(nombreFijo || '');
  const [dni, setDni] = useState(dniInicial || '');
  const [foto, setFoto] = useState(null);
  const [procesando, setProcesando] = useState(false);
  const [error, setError] = useState('');

  useEffect(() => {
    if (modo !== 'dibujar') return undefined;
    const c = lienzoRef.current;
    if (!c) return undefined;

    // Ajustar a la densidad de la pantalla para que el trazo no salga
    // pixelado en el celular.
    const dpr = window.devicePixelRatio || 1;
    const rect = c.getBoundingClientRect();
    c.width = rect.width * dpr;
    c.height = rect.height * dpr;

    const ctx = c.getContext('2d');
    ctx.scale(dpr, dpr);
    ctx.lineWidth = 2.2;
    ctx.lineCap = 'round';
    ctx.lineJoin = 'round';
    ctx.strokeStyle = '#0A0F1A';

    let dibujando = false;
    const punto = (e) => {
      const r = c.getBoundingClientRect();
      const t = e.touches?.[0] || e;
      return { x: t.clientX - r.left, y: t.clientY - r.top };
    };
    const empezar = (e) => {
      e.preventDefault();
      dibujando = true;
      const p = punto(e);
      ctx.beginPath();
      ctx.moveTo(p.x, p.y);
    };
    const mover = (e) => {
      if (!dibujando) return;
      e.preventDefault();
      const p = punto(e);
      ctx.lineTo(p.x, p.y);
      ctx.stroke();
      setVacio(false);
    };
    const terminar = () => { dibujando = false; };

    c.addEventListener('mousedown', empezar);
    c.addEventListener('mousemove', mover);
    window.addEventListener('mouseup', terminar);
    c.addEventListener('touchstart', empezar, { passive: false });
    c.addEventListener('touchmove', mover, { passive: false });
    c.addEventListener('touchend', terminar);

    return () => {
      c.removeEventListener('mousedown', empezar);
      c.removeEventListener('mousemove', mover);
      window.removeEventListener('mouseup', terminar);
      c.removeEventListener('touchstart', empezar);
      c.removeEventListener('touchmove', mover);
      c.removeEventListener('touchend', terminar);
    };
  }, [modo]);

  const limpiar = () => {
    const c = lienzoRef.current;
    if (c) c.getContext('2d').clearRect(0, 0, c.width, c.height);
    setVacio(true);
  };

  // Foto de la firma en papel: se le quita el fondo antes de guardarla.
  // Sin eso, al imprimirla saldría un recuadro blanco tapando la línea
  // del documento.
  const desdeFoto = async (e) => {
    const archivo = e.target.files?.[0];
    e.target.value = '';
    if (!archivo) return;

    setProcesando(true);
    setError('');
    setFoto(null);
    try {
      const r = await procesarFotoFirma(archivo);
      setFoto(r);
      if (r.aviso) setError(r.aviso);
    } catch (err) {
      setError(err.message);
    }
    setProcesando(false);
  };

  const listo = modo === 'dibujar' ? !vacio : Boolean(foto);

  const guardar = () => {
    if (!listo || !nombre.trim()) return;
    const trazo = modo === 'dibujar'
      ? lienzoRef.current.toDataURL('image/png').split(',')[1]
      : foto.base64;
    onGuardar({ trazo, firmanteNombre: nombre.trim(), firmanteDni: dni.trim() || null });
  };

  const pestana = (id, texto) => (
    <button key={id} type="button" onClick={() => { setModo(id); setError(''); }}
            style={{
              flex: 1, padding: '9px 12px', fontSize: 13, cursor: 'pointer',
              border: 'none', borderBottom: `2px solid ${modo === id ? T.navy : 'transparent'}`,
              background: 'none',
              color: modo === id ? T.navy : T.muted,
              fontWeight: modo === id ? 600 : 400,
            }}>
      {texto}
    </button>
  );

  return (
    <div style={{
      position: 'fixed', inset: 0, zIndex: 120, background: 'rgba(10,15,26,.9)',
      display: 'flex', alignItems: 'center', justifyContent: 'center', padding: 18,
      overflowY: 'auto',
    }}>
      <div style={{
        background: '#fff', borderRadius: 12, padding: 20,
        width: '100%', maxWidth: 420, maxHeight: '94vh', overflowY: 'auto',
      }}>
        <div style={{
          fontFamily: serif, fontSize: 20, color: T.navy,
          fontWeight: 600, marginBottom: 14,
        }}>
          {titulo}
        </div>

        {nombreFijo ? (
          <div style={{ fontSize: 14, color: T.navy, fontWeight: 600, marginBottom: 14 }}>
            {nombreFijo}
          </div>
        ) : (
          <Campo etiqueta="Nombre de quien firma">
            <input value={nombre} onChange={(e) => setNombre(e.target.value)}
                   placeholder="Apellidos y nombres" style={estiloInput} />
          </Campo>
        )}

        <Campo etiqueta="DNI" ayuda="Opcional.">
          <input value={dni} inputMode="numeric" maxLength={8}
                 onChange={(e) => setDni(e.target.value.replace(/\D/g, ''))}
                 style={estiloInput} />
        </Campo>

        {/* Dos caminos: trazar aquí, o subir la firma de papel */}
        <div style={{
          display: 'flex', borderBottom: `1px solid ${T.border}`, marginBottom: 14,
        }}>
          {pestana('dibujar', 'Dibujarla')}
          {pestana('foto', 'Subir una foto')}
        </div>

        {modo === 'dibujar' ? (
          <>
            <canvas
              ref={lienzoRef}
              style={{
                width: '100%', height: 160, display: 'block',
                border: `1px dashed ${T.border}`, borderRadius: 8,
                background: T.warm, touchAction: 'none', cursor: 'crosshair',
              }}
            />
            <div style={{ fontSize: 11, color: T.muted, marginTop: 6 }}>
              Firma con el dedo si estás en el celular, o con el ratón.
            </div>
          </>
        ) : (
          <>
            {foto ? (
              <div style={{
                border: `1px solid ${T.border}`, borderRadius: 8,
                background: '#fff', padding: 12, textAlign: 'center',
              }}>
                {/* Fondo a cuadros para que se note que quedó transparente */}
                <div style={{
                  backgroundImage:
                    'linear-gradient(45deg, #eee 25%, transparent 25%),'
                  + 'linear-gradient(-45deg, #eee 25%, transparent 25%),'
                  + 'linear-gradient(45deg, transparent 75%, #eee 75%),'
                  + 'linear-gradient(-45deg, transparent 75%, #eee 75%)',
                  backgroundSize: '14px 14px',
                  backgroundPosition: '0 0, 0 7px, 7px -7px, -7px 0',
                  padding: 10, borderRadius: 6,
                }}>
                  <img src={`data:image/png;base64,${foto.base64}`} alt="Tu firma"
                       style={{ maxHeight: 110, maxWidth: '100%' }} />
                </div>
                <div style={{ fontSize: 11, color: T.muted, marginTop: 8 }}>
                  El fondo del papel se quitó. Así se verá sobre el documento.
                </div>
              </div>
            ) : (
              <div
                onClick={() => !procesando && archivoRef.current?.click()}
                style={{
                  border: `1px dashed ${T.border}`, borderRadius: 8,
                  background: T.warm, padding: '32px 16px', textAlign: 'center',
                  cursor: procesando ? 'default' : 'pointer',
                }}>
                <div style={{ fontSize: 14, color: T.navy, fontWeight: 600 }}>
                  {procesando ? 'Limpiando la foto…' : 'Tomar la foto o elegir una del carrete'}
                </div>
                <div style={{ fontSize: 12, color: T.muted, marginTop: 6, lineHeight: 1.5 }}>
                  Firma en una hoja blanca con lapicero oscuro y fotografíala de frente
                  con buena luz. Si ya la tienes guardada, elígela del carrete.
                </div>
              </div>
            )}

            {foto && (
              <Boton variante="suave" onClick={() => archivoRef.current?.click()}
                     style={{ width: '100%', marginTop: 10 }}>
                Cambiar la foto
              </Boton>
            )}

            <input ref={archivoRef} type="file" accept="image/*"
                   onChange={desdeFoto} style={{ display: 'none' }} />
          </>
        )}

        {error && (
          <div style={{ marginTop: 12 }}>
            <Aviso tipo="warn">{error}</Aviso>
          </div>
        )}

        <div style={{ display: 'flex', gap: 8, marginTop: 16, flexWrap: 'wrap' }}>
          {modo === 'dibujar' ? (
            <Boton variante="suave" onClick={limpiar} style={{ flex: 1 }}>Borrar</Boton>
          ) : (
            <Boton variante="suave" onClick={() => { setFoto(null); setError(''); }}
                   disabled={!foto} style={{ flex: 1 }}>
              Quitar
            </Boton>
          )}
          <Boton variante="suave" onClick={onCancelar} style={{ flex: 1 }}>Cancelar</Boton>
          <Boton variante="acento" onClick={guardar}
                 disabled={!listo || !nombre.trim()} style={{ flex: 1.4 }}>
            Firmar
          </Boton>
        </div>
      </div>
    </div>
  );
}

// ── DOCUMENTO IMPRIMIBLE ────────────────────────────────────────────
export function DocumentoImprimible({ entidad, id, onCerrar }) {
  const [d, setD] = useState(null);
  const [firmando, setFirmando] = useState(null);
  const [aviso, setAviso] = useState(null);

  const cargar = () => {
    const f = {
      gasto: () => api.documentoGasto(id),
      viatico: () => api.documentoViatico(id),
      encargo: () => api.documentoEncargo(id),
    }[entidad];
    f().then(setD).catch(() => setD({}));
  };

  useEffect(() => { cargar(); }, [entidad, id]);   // eslint-disable-line

  // Antes esto marcaba el documento como impreso ANTES de abrir el
  // diálogo. Si la hoja salía en blanco o se cancelaba, quedaba
  // contado igual. El navegador no puede saber si salió papel; quien
  // tiene la hoja delante, sí.
  const [preguntandoImpresion, setPreguntandoImpresion] = useState(false);

  const imprimir = () => {
    window.print();
    setPreguntandoImpresion(true);
  };

  const confirmarImpreso = async () => {
    setPreguntandoImpresion(false);
    if (entidad !== 'encargo') {
      await api.marcarImpreso(entidad, id).catch(() => {});
    }
    setAviso({ tipo: 'ok', texto: 'Documento marcado como impreso.' });
  };

  const firmar = async (datos) => {
    try {
      await api.firmarDocumento({
        entidad, entidadId: id,
        documento: nombreDocumento(entidad, d),
        rolFirma: firmando,
        huella: d.huella,
        manuscrita: false,
        ...datos,
      });
      setFirmando(null);
      setAviso({ tipo: 'ok', texto: 'Firma registrada.' });
      cargar();
    } catch (err) {
      setFirmando(null);
      setAviso({
        tipo: 'bad',
        texto: err.detalle?.detalle
          || 'No se pudo registrar la firma.',
      });
    }
  };

  if (!d) return null;

  // La hoja va DIRECTAMENTE en <body>: el CSS de impresión oculta
  // `body > *:not(.hoja)`, y dentro de #root esa regla ocultaba al
  // propio #root —o sea, todo— y la página salía en blanco.
  return createPortal(
    <div style={{
      position: 'fixed', inset: 0, zIndex: 110,
      background: '#fff', overflowY: 'auto',
    }} className="hoja">
      <style>{ESTILO_IMPRESION}</style>

      {preguntandoImpresion && (
        <ConfirmarSalioImpreso
          onSalioBien={confirmarImpreso}
          onReintentar={() => setPreguntandoImpresion(false)}
        />
      )}

      {/* Barra de acciones: no se imprime */}
      <div className="no-imprimir" style={{
        position: 'sticky', top: 0, background: T.navy,
        padding: '11px 16px', display: 'flex', gap: 8,
        alignItems: 'center', flexWrap: 'wrap', zIndex: 5,
      }}>
        <Boton variante="suave" onClick={onCerrar}
               style={{ fontSize: 13, padding: '6px 13px' }}>
          Cerrar
        </Boton>
        <Boton variante="acento" onClick={imprimir}
               style={{ fontSize: 13, padding: '6px 13px' }}>
          Imprimir o guardar PDF
        </Boton>

        {!d.exigeManuscrita && (
          <>
            <Boton variante="suave" onClick={() => setFirmando('emisor')}
                   style={{ fontSize: 13, padding: '6px 13px' }}>
              Firmar como emisor
            </Boton>
            <Boton variante="suave" onClick={() => setFirmando('receptor')}
                   style={{ fontSize: 13, padding: '6px 13px' }}>
              Firmar como receptor
            </Boton>
          </>
        )}
      </div>

      {aviso && (
        <div className="no-imprimir" style={{ padding: '12px 16px 0' }}>
          <Aviso tipo={aviso.tipo}>{aviso.texto}</Aviso>
        </div>
      )}

      {d.exigeManuscrita && (
        <div className="no-imprimir" style={{ padding: '12px 16px 0' }}>
          <Aviso tipo="warn">
            La planilla de movilidad necesita la firma manuscrita del trabajador:
            así lo exige el Reglamento del Impuesto a la Renta. Imprímela, fírmala
            en papel y adjunta la foto al gasto. Una firma en pantalla no sirve
            para sustentar este documento ante SUNAT.
          </Aviso>
        </div>
      )}

      <div className="documento">
        <Encabezado />
        {entidad === 'gasto' && <CuerpoGasto d={d} />}
        {entidad === 'viatico' && <CuerpoViatico d={d} />}
        {entidad === 'encargo' && <CuerpoEncargo d={d} />}
        <Firmas d={d} />
        <PiePagina d={d} entidad={entidad} id={id} />
      </div>

      {firmando && (
        <PadFirma
          titulo={firmando === 'emisor' ? 'Firma de quien entrega' : 'Firma de quien recibe'}
          onGuardar={firmar}
          onCancelar={() => setFirmando(null)}
        />
      )}
    </div>,
    document.body,
  );
}


// ── ¿Salió el papel? ────────────────────────────────────────────────
function ConfirmarSalioImpreso({ onSalioBien, onReintentar }) {
  return (
    <div className="no-imprimir" style={{
      position: 'fixed', inset: 0, zIndex: 140, background: 'rgba(10,15,26,.72)',
      display: 'flex', alignItems: 'center', justifyContent: 'center', padding: 16,
    }}>
      <div style={{
        background: '#fff', borderRadius: 12, padding: 22,
        width: '100%', maxWidth: 400,
      }}>
        <div style={{ fontSize: 18, fontWeight: 600, color: T.navy, marginBottom: 6 }}>
          ¿Salió el papel?
        </div>
        <div style={{ fontSize: 13, color: T.muted, marginBottom: 18, lineHeight: 1.5 }}>
          Revisa que la hoja tenga el contenido completo, no solo el
          encabezado. Si salió en blanco, el documento se queda pendiente.
        </div>
        <div style={{ display: 'flex', flexDirection: 'column', gap: 8 }}>
          <Boton variante="acento" onClick={onSalioBien} style={{ width: '100%' }}>
            Sí, salió completa
          </Boton>
          <Boton variante="suave" onClick={onReintentar} style={{ width: '100%' }}>
            No, dejarlo pendiente
          </Boton>
        </div>
      </div>
    </div>
  );
}

function nombreDocumento(entidad, d) {
  if (entidad === 'viatico') return 'viaticos';
  if (entidad === 'encargo') return 'liquidacion';
  return d?.gasto?.tipoDocumento === 'planilla_movilidad'
    ? 'planilla_movilidad' : 'recibo';
}

// ── Piezas del documento ────────────────────────────────────────────
function Encabezado() {
  return (
    <div style={{
      display: 'flex', justifyContent: 'space-between',
      alignItems: 'flex-start', gap: 16, paddingBottom: 12,
      borderBottom: '2px solid #0D2547', marginBottom: 18,
    }}>
      <img src="/marca/logo-acceso.png" alt="SICREA" style={{ height: 44 }} />
      <div style={{ textAlign: 'right', fontSize: 10, lineHeight: 1.5, color: '#444' }}>
        <div style={{ fontWeight: 700, color: '#0D2547' }}>SICREA S.A.C.</div>
        <div>RUC 20136792319</div>
        <div>Calle A Mz. T Lt. 4 Urb. Nuevo Lurín</div>
        <div>Lurín, Lima · www.sicrea.net</div>
      </div>
    </div>
  );
}

function TituloDoc({ children, numero }) {
  return (
    <div style={{
      display: 'flex', justifyContent: 'space-between',
      alignItems: 'baseline', marginBottom: 16, gap: 12,
    }}>
      <div style={{
        fontSize: 17, fontWeight: 700, color: '#0D2547',
        letterSpacing: '.02em',
      }}>
        {children}
      </div>
      {numero && (
        <div style={{
          fontFamily: 'monospace', fontSize: 16,
          fontWeight: 700, color: '#B8241C',
        }}>
          N° {numero}
        </div>
      )}
    </div>
  );
}

function Campos({ filas }) {
  return (
    <table style={{ width: '100%', borderCollapse: 'collapse', marginBottom: 14 }}>
      <tbody>
        {filas.filter(Boolean).map(([k, v], i) => (
          <tr key={i}>
            <td style={{
              padding: '5px 8px 5px 0', fontSize: 11, color: '#666',
              width: '30%', verticalAlign: 'top',
            }}>
              {k}
            </td>
            <td style={{
              padding: '5px 0', fontSize: 12, color: '#111',
              borderBottom: '1px dotted #bbb',
            }}>
              {v ?? '—'}
            </td>
          </tr>
        ))}
      </tbody>
    </table>
  );
}

function CuerpoGasto({ d }) {
  const g = d.gasto || {};
  const esMovilidad = g.tipoDocumento === 'planilla_movilidad';

  if (esMovilidad) {
    return (
      <>
        <TituloDoc numero={g.numeroDocumento}>PLANILLA POR GASTO DE MOVILIDAD</TituloDoc>
        <Campos filas={[
          ['Trabajador', d.autor?.nombre],
          ['Centro', g.centro],
          ['Orden de venta', g.ovId || 'Sin OV'],
          ['Medio utilizado', g.medioTransporte],
        ]} />

        <table style={tablaEstilo}>
          <thead>
            <tr>
              {['Día', 'De', 'A ó para', 'Motivo', 'Importe'].map((h) => (
                <th key={h} style={thEstilo}>{h}</th>
              ))}
            </tr>
          </thead>
          <tbody>
            {(d.trayectos || []).map((t) => (
              <tr key={t.id}>
                <td style={tdEstilo}>{fmt.fecha(t.fecha)}</td>
                <td style={tdEstilo}>{t.origen}</td>
                <td style={tdEstilo}>{t.destino}</td>
                <td style={tdEstilo}>{t.motivo}</td>
                <td style={{ ...tdEstilo, textAlign: 'right' }}>
                  {Number(t.importe).toFixed(2)}
                </td>
              </tr>
            ))}
            <tr>
              <td colSpan={4} style={{ ...tdEstilo, textAlign: 'right', fontWeight: 700 }}>
                TOTAL S/
              </td>
              <td style={{ ...tdEstilo, textAlign: 'right', fontWeight: 700 }}>
                {Number(g.total).toFixed(2)}
              </td>
            </tr>
          </tbody>
        </table>
      </>
    );
  }

  const esRecibo = g.tipoDocumento === 'recibo_ad';
  return (
    <>
      <TituloDoc numero={esRecibo ? `${g.reciboSerie || 'AD'}-${g.numeroDocumento || ''}` : g.id}>
        {esRecibo ? 'RECIBO' : 'CONSTANCIA DE GASTO'}
      </TituloDoc>

      {esRecibo && (
        <div style={{ fontSize: 12, marginBottom: 12 }}>
          Recibí de <strong>SICREA S.A.C.</strong> la cantidad de{' '}
          <strong style={{ fontSize: 15 }}>{fmt.money(g.total, g.moneda)}</strong>
        </div>
      )}

      <Campos filas={[
        ['Fecha', fmt.fecha(g.fecha)],
        ['Concepto', g.concepto],
        ['Cuenta contable', `${g.cuentaPcge || ''} · ${d.cuentaNombre || ''}`],
        ['Orden de venta', g.ovId ? `${g.ovId}${d.ov ? ` · ${d.ov.clienteAlias}` : ''}` : 'Sin OV'],
        ['Centro', g.centro],
        ['Medio de pago', g.medioPago],
        !esRecibo && ['Importe', fmt.money(g.total, g.moneda)],
        (d.beneficiario?.nombre || g.proveedorRazon) && ['Beneficiario', d.beneficiario?.nombre || g.proveedorRazon],
        (d.beneficiario?.dni || g.proveedorRuc) && ['DNI o RUC', d.beneficiario?.dni || g.proveedorRuc],
        d.beneficiario?.cargo && ['Cargo', d.beneficiario.cargo],
        ['Rindente', d.autor?.nombre],
      ]} />
    </>
  );
}

function CuerpoViatico({ d }) {
  const s = d.solicitud || {};
  const CONC = [
    ['desayuno', 'Desayuno'], ['almuerzo', 'Almuerzo'], ['cena', 'Cena'],
    ['transporte', 'Transporte'], ['agua', 'Agua'], ['otros', 'Otros'],
  ];
  const suma = (c) => (d.personas || []).reduce((a, p) => a + Number(p[c] || 0), 0);

  return (
    <>
      <TituloDoc numero={s.numero}>FORMATO DE ENTREGA DE VIÁTICOS</TituloDoc>

      <table style={{ width: '100%', marginBottom: 14, fontSize: 11 }}>
        <tbody>
          <tr>
            <td style={{ width: '55%', verticalAlign: 'top' }}>
              <Campos filas={[
                ['Cliente', s.cliente],
                ['OP', s.tipoOp],
                ['Lugar', s.lugar],
                ['Orden de venta', s.ovId || 'Sin OV'],
              ]} />
            </td>
            <td style={{ verticalAlign: 'top', paddingLeft: 14 }}>
              <Campos filas={[
                ['Código', s.codigoFormato || 'SICREAFO34'],
                ['Del', fmt.fecha(s.fechaInicio)],
                ['Al', fmt.fecha(s.fechaFin)],
                ['Días', s.dias],
              ]} />
            </td>
          </tr>
        </tbody>
      </table>

      <table style={tablaEstilo}>
        <thead>
          <tr>
            <th style={{ ...thEstilo, width: 26 }}>#</th>
            <th style={thEstilo}>Personal</th>
            {CONC.map(([c, t]) => <th key={c} style={thEstilo}>{t}</th>)}
            <th style={thEstilo}>Subtotal</th>
          </tr>
        </thead>
        <tbody>
          {(d.personas || []).map((p, i) => (
            <tr key={p.id}>
              <td style={tdEstilo}>{i + 1}</td>
              <td style={tdEstilo}>{p.nombre}</td>
              {CONC.map(([c]) => (
                <td key={c} style={{ ...tdEstilo, textAlign: 'right' }}>
                  {Number(p[c]) > 0 ? Number(p[c]).toFixed(2) : ''}
                </td>
              ))}
              <td style={{ ...tdEstilo, textAlign: 'right', fontWeight: 600 }}>
                {Number(p.subtotal).toFixed(2)}
              </td>
            </tr>
          ))}
          <tr>
            <td colSpan={2} style={{ ...tdEstilo, fontWeight: 700 }}>Subtotales por día</td>
            {CONC.map(([c]) => (
              <td key={c} style={{ ...tdEstilo, textAlign: 'right', fontWeight: 700 }}>
                {suma(c) > 0 ? suma(c).toFixed(2) : ''}
              </td>
            ))}
            <td style={tdEstilo} />
          </tr>
        </tbody>
      </table>

      <div style={{ display: 'flex', gap: 16, marginTop: 14 }}>
        <div style={{ flex: 1, fontSize: 11 }}>
          <div style={{ color: '#666', marginBottom: 4 }}>OBSERVACIONES</div>
          <div style={{
            border: '1px solid #ccc', minHeight: 62, padding: 8, fontSize: 11,
          }}>
            {s.observaciones}
          </div>
        </div>
        <div style={{ width: 190 }}>
          <table style={{ width: '100%', borderCollapse: 'collapse', fontSize: 11 }}>
            <tbody>
              <tr>
                <td style={tdEstilo}>Cochera</td>
                <td style={{ ...tdEstilo, textAlign: 'right' }}>
                  {Number(s.cochera).toFixed(2)}
                </td>
              </tr>
              <tr>
                <td style={{ ...tdEstilo, fontWeight: 700 }}>TOTAL</td>
                <td style={{ ...tdEstilo, textAlign: 'right', fontWeight: 700, fontSize: 13 }}>
                  {fmt.money(s.total, s.moneda)}
                </td>
              </tr>
            </tbody>
          </table>
        </div>
      </div>

      <div style={{ fontSize: 10, marginTop: 12, fontWeight: 600 }}>
        Se debe rendir al término del trabajo.
      </div>
    </>
  );
}

function CuerpoEncargo({ d }) {
  const e = d.encargo || {};
  return (
    <>
      <TituloDoc numero={e.id}>LIQUIDACIÓN DE ENTREGA A RENDIR</TituloDoc>

      <Campos filas={[
        ['Responsable', d.titular?.nombre],
        ['Concepto', e.glosa],
        ['Orden de venta', e.ovId || 'Sin OV'],
        ['Apertura', fmt.fecha(e.fechaApertura)],
        ['Vencimiento', fmt.fecha(e.vencimiento)],
      ]} />

      <table style={tablaEstilo}>
        <thead>
          <tr>
            <th style={thEstilo}>Fecha</th>
            <th style={thEstilo}>Concepto</th>
            <th style={thEstilo}>Proveedor</th>
            <th style={thEstilo}>Importe</th>
          </tr>
        </thead>
        <tbody>
          {(d.detalle || []).map((g) => (
            <tr key={g.id}>
              <td style={tdEstilo}>{fmt.fecha(g.fecha)}</td>
              <td style={tdEstilo}>{g.concepto || g.cuentaNombre}</td>
              <td style={tdEstilo}>{g.proveedor || '—'}</td>
              <td style={{ ...tdEstilo, textAlign: 'right' }}>
                {Number(g.total).toFixed(2)}
              </td>
            </tr>
          ))}
        </tbody>
      </table>

      <table style={{
        width: 250, marginLeft: 'auto', marginTop: 14,
        borderCollapse: 'collapse', fontSize: 12,
      }}>
        <tbody>
          <tr>
            <td style={tdEstilo}>Entregado</td>
            <td style={{ ...tdEstilo, textAlign: 'right' }}>
              {fmt.money(d.entregado, e.moneda)}
            </td>
          </tr>
          <tr>
            <td style={tdEstilo}>Rendido</td>
            <td style={{ ...tdEstilo, textAlign: 'right' }}>
              {fmt.money(d.rendido, e.moneda)}
            </td>
          </tr>
          <tr>
            <td style={{ ...tdEstilo, fontWeight: 700 }}>
              {d.saldo >= 0 ? 'Saldo por devolver' : 'A reembolsar'}
            </td>
            <td style={{ ...tdEstilo, textAlign: 'right', fontWeight: 700, fontSize: 14 }}>
              {fmt.money(Math.abs(d.saldo), e.moneda)}
            </td>
          </tr>
        </tbody>
      </table>
    </>
  );
}

function Firmas({ d }) {
  const firmas = d.firmas || [];
  const buscar = (rol) => firmas.find((f) => f.rolFirma === rol);

  return (
    <div style={{
      display: 'flex', gap: 40, marginTop: 42,
      justifyContent: 'space-around', pageBreakInside: 'avoid',
    }}>
      {[['emisor', 'Entregado por'], ['receptor', 'Recibido conforme']].map(([rol, texto]) => {
        const f = buscar(rol);
        return (
          <div key={rol} style={{ flex: 1, maxWidth: 230, textAlign: 'center' }}>
            <div style={{ height: 58, display: 'flex', alignItems: 'flex-end', justifyContent: 'center' }}>
              {f?.trazo && (
                <img src={`data:image/png;base64,${f.trazo}`} alt=""
                     style={{ maxHeight: 56, maxWidth: '100%' }} />
              )}
            </div>
            <div style={{ borderTop: '1px solid #333', paddingTop: 5 }}>
              <div style={{ fontSize: 10, color: '#666' }}>{texto}</div>
              <div style={{ fontSize: 11, fontWeight: 600, minHeight: 14 }}>
                {f?.firmanteNombre || ''}
              </div>
              <div style={{ fontSize: 10, color: '#666' }}>
                {f?.firmanteDni ? `DNI ${f.firmanteDni}` : 'DNI ______________'}
              </div>
              {f && (
                <div style={{ fontSize: 8, color: '#999', marginTop: 3 }}>
                  Firmado en plataforma · {fmt.fechaHora(f.createdAt)}
                </div>
              )}
            </div>
          </div>
        );
      })}
    </div>
  );
}

function PiePagina({ d, entidad, id }) {
  return (
    <div style={{
      marginTop: 30, paddingTop: 8, borderTop: '1px solid #ddd',
      fontSize: 8, color: '#888', display: 'flex',
      justifyContent: 'space-between', gap: 12,
    }}>
      <span>
        SICREA RINDE · {entidad} {id}
        {d.huella ? ` · huella ${d.huella.slice(0, 12)}` : ''}
      </span>
      <span>Emitido {new Date().toLocaleString('es-PE')}</span>
    </div>
  );
}

// ── Estilos ─────────────────────────────────────────────────────────
const tablaEstilo = {
  width: '100%', borderCollapse: 'collapse', fontSize: 11, marginBottom: 8,
};
const thEstilo = {
  border: '1px solid #999', padding: '5px 6px', background: '#eceff4',
  fontSize: 10, fontWeight: 700, textAlign: 'left', color: '#0D2547',
};
const tdEstilo = {
  border: '1px solid #ccc', padding: '5px 6px', fontSize: 11, verticalAlign: 'top',
};

const ESTILO_IMPRESION = `
.documento {
  max-width: 780px;
  margin: 0 auto;
  padding: 26px 22px 40px;
  background: #fff;
  color: #111;
  font-family: system-ui, -apple-system, sans-serif;
}
@media print {
  /* Solo se imprime el documento: el resto de la aplicacion estorba */
  body > *:not(.hoja) { display: none !important; }
  .no-imprimir { display: none !important; }
  .hoja { position: static !important; }
  .documento { max-width: none; padding: 0; }
  @page { size: A4; margin: 14mm; }
}
`;
